import { useQuery } from "@tanstack/react-query";
import { Tab, Tabs } from "@monolith-forensics/monolith-ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { getTimeEntryReport, getTimeEntryReportMetrics } from "../../../../api";
import TimeEntriesAPI from "../../../../api/TimeEntries/time-entries.js";
import { EditReportPopup } from "../../EditReportPopup";
import { SavedReports } from "./SavedReports";
import { TimeEntries } from "./TimeEntries";
import { TimeEntryComposition } from "./TimeEntryComposition";
import { TimeEntryDetails } from "./TimeEntryDetails";
import { TimeEntryMetrics } from "./TimeEntryMetrics";
import ReportParametersPanel, {
  TIME_ENTRY_PARAMS_FIELDS,
} from "../ReportParametersPanel";
import { UNASSIGNED_FILTER_VALUE } from "../../params/filter-constants";

export const TimeEntryReport = ({ reportInfo, setFormUpdated }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const editReportPopup = useRef(null);

  // Category filter values are stored as ids; resolve names for the params
  // panel via the same lookup the edit form uses.
  const { data: categories } = useQuery({
    queryKey: ["time:categories"],
    queryFn: () => TimeEntriesAPI.getTimeCategories(),
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const filterFields = useMemo(
    () =>
      TIME_ENTRY_PARAMS_FIELDS.map((field) =>
        field.key === "category"
          ? {
              ...field,
              format: (values) =>
                values
                  .map((id) => {
                    if (id === UNASSIGNED_FILTER_VALUE)
                      return field.unassignedLabel;
                    const match = categories.find((c) => c.category_id === id);
                    return match ? match.category_name : id;
                  })
                  .join(", "),
            }
          : field
      ),
    [categories]
  );

  const { isLoading, data: timeEntry } = useQuery({
    queryKey: [
      "reports:time-entries-overview:entries",
      reportInfo.report_parameters,
    ],
    queryFn: () => getTimeEntryReport(reportInfo.report_parameters),
  });

  const { isLoading: metricsLoading, data: metrics } = useQuery({
    queryKey: [
      "reports:time-entries-overview:metrics",
      {
        ...reportInfo.report_parameters,
        dimension: "composition",
      },
    ],
    queryFn: () =>
      getTimeEntryReportMetrics({
        ...reportInfo.report_parameters,
        dimension: "composition",
      }),
  });

  return (
    <div>
      <div
        style={{
          display: "flex",
          marginBottom: "20px",
          userSelect: "text",
        }}
      >
        <TimeEntryDetails reportInfo={reportInfo} />
        <ReportParametersPanel
          reportInfo={reportInfo}
          editReportPopup={editReportPopup}
          filterFields={filterFields}
        />
        <TimeEntryMetrics timeEntry={timeEntry} metrics={metrics} />
      </div>

      <TimeEntryComposition metrics={metrics} />

      <div
        style={{
          minHeight: "450px",
          margin: "20px 0px",
        }}
      >
        <Tabs value={selectedIndex} onChange={setSelectedIndex}>
          <Tab value={0}>Saved Reports</Tab>
          <Tab value={1}>Time Entries</Tab>
        </Tabs>

        {selectedIndex === 0 && <SavedReports reportInfo={reportInfo} />}
        {selectedIndex === 1 && (
          <TimeEntries reportInfo={reportInfo} timeEntry={timeEntry} />
        )}
      </div>

      <EditReportPopup
        editReportPopup={editReportPopup}
        reportInfo={reportInfo}
        setFormUpdated={setFormUpdated}
      />
    </div>
  );
};
